/* eslint-disable no-mixed-spaces-and-tabs */
import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';
import { getFormValues, isInvalid } from 'redux-form';
import { initialState } from './reducer';
import { ADMIN_ROLE_NUMBER, ADMIN_TABLE_ROWS_NAMES, FORM_NAME, TABLE_ROWS_NAMES } from './constants';
import { thousandSeparator } from '../reqs-filter/utils';
import { VEEROUTE_URL } from '../../api/constants';

export const selectOrdersWaysModalDomain = state => state.ordersWaysModalReducer || initialState;
export const selectRouterDomain = state => state.router || {};
export const selectProfileModalDomain = state => state.profileModalReducer || {};

export const getIsAdminSelector = () =>
	createSelector(selectProfileModalDomain, ({ userInfo }) => userInfo?.company?.id === ADMIN_ROLE_NUMBER);

export const getCompanyIdSelector = () =>
	createSelector(selectProfileModalDomain, ({ userInfo }) => userInfo?.company?.id);

export const getOrdersWaysInfoSelector = () =>
	createSelector(selectOrdersWaysModalDomain, getIsAdminSelector(), ({ ordersWaysInfo }, isAdmin) => {
		if (!ordersWaysInfo) {
			return [];
		}

		const getSubValue = value => (value !== null && value !== undefined ? value : '---');

		const data = {
			id: ordersWaysInfo.id,
			orderGroupTitle: ordersWaysInfo.group?.name ?? '---',
			autopark:
				ordersWaysInfo.transport?.autopark && ordersWaysInfo.transport?.autopark?.tk
					? `${ordersWaysInfo.transport?.autopark?.tk.name}${
							ordersWaysInfo.transport?.autopark?.name
								? `, ${ordersWaysInfo.transport?.autopark?.name}`
								: ''
					  }`
					: '---',
			transport: ordersWaysInfo.transport?.name ?? '---',
			maxWeight: ordersWaysInfo.weight ? `${ordersWaysInfo.weight} кг` : '---',
			maxVolume: ordersWaysInfo.volume ? `${ordersWaysInfo.volume} м³` : '---',
			weightRate: ordersWaysInfo.weight_rate ?? '---',
			volumeRate: ordersWaysInfo.volume_rate ?? '---',
			timeWindowFrom: ordersWaysInfo?.time_window_from
				? new Date(ordersWaysInfo?.time_window_from).toLocaleString('ru-RU', {
						year: 'numeric',
						month: '2-digit',
						day: '2-digit',
						hour: '2-digit',
						minute: '2-digit',
						second: '2-digit',
				  })
				: '---',
			timeWindowTo: ordersWaysInfo?.time_window_to
				? new Date(ordersWaysInfo?.time_window_to).toLocaleString('ru-RU', {
						year: 'numeric',
						month: '2-digit',
						day: '2-digit',
						hour: '2-digit',
						minute: '2-digit',
						second: '2-digit',
				  })
				: '---',
			distance: ordersWaysInfo.distance ? `${thousandSeparator(ordersWaysInfo.distance)} км` : '---',
			totalTime: ordersWaysInfo.total_time ?? '---',
			sumCost: ordersWaysInfo.orders_sum_cost
				? `${thousandSeparator(ordersWaysInfo.orders_sum_cost)} руб`
				: '---',
			sumWeight: ordersWaysInfo.orders_sum_weight
				? `${ordersWaysInfo.orders_sum_weight_with_rate} / ${ordersWaysInfo.weight_with_recycling ?? '---'} кг`
				: '---',
			sumVolume: ordersWaysInfo.orders_sum_volume
				? `${ordersWaysInfo.orders_sum_volume_with_rate} / ${ordersWaysInfo.volume_with_recycling ?? '---'} м³`
				: '---',
			link: ordersWaysInfo.experiment_key
				? {
						text: 'Перейти',
						target: '_blank',
						url: `${VEEROUTE_URL}/experiments/${ordersWaysInfo.experiment_key}`,
				  }
				: '---',
		};

		const rowsNames = isAdmin ? ADMIN_TABLE_ROWS_NAMES : TABLE_ROWS_NAMES;

		return Object.entries(data).map(([key, value]) => {
			if (key === 'maxWeight') {
				return {
					id: key,
					title: rowsNames[key] || key,
					value,
					sub_value: getSubValue(ordersWaysInfo?.weight_recycling_rate),
				};
			}

			if (key === 'maxVolume') {
				return {
					id: key,
					title: rowsNames[key] || key,
					value,
					sub_value: getSubValue(ordersWaysInfo?.volume_recycling_rate),
				};
			}

			return {
				id: key,
				title: rowsNames[key] || key,
				value,
			};
		});
	});

export const getOrdersWaysNameSelector = () =>
	createSelector(selectOrdersWaysModalDomain, ({ ordersWaysInfo }) => {
		return ordersWaysInfo ? ordersWaysInfo.name : '';
	});

export const getIsOrdersWaysTrashedSelector = () =>
	createSelector(selectOrdersWaysModalDomain, ({ ordersWaysInfo }) => {
		return !!ordersWaysInfo?.deleted_at;
	});

export const getActiveOrdersWaysIdSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return query?.modal === 'order-way' ? query?.id : null;
	});

export const getIsOptionsListLoadingSelector = () =>
	createSelector(selectOrdersWaysModalDomain, ({ isCarriersListLoading }) => ({
		carriers: isCarriersListLoading,
	}));

export const getIsOptionsLoadingSelector = () =>
	createSelector(
		selectOrdersWaysModalDomain,
		({ isCarriersListLoading, isShippersListLoading, isSettingsListLoading, isCategoriesListLoading }) =>
			isShippersListLoading || isCarriersListLoading || isCategoriesListLoading || isSettingsListLoading,
	);

export const getOptionsListSelector = () =>
	createSelector(selectOrdersWaysModalDomain, ({ groupList, ordersWaysList }) => ({
		group: groupList,
		ordersWays: ordersWaysList,
	}));

export const getFormInitialValuesSelector = () =>
	createSelector(
		selectOrdersWaysModalDomain,
		getIsAdminSelector(),
		getCompanyIdSelector(),
		getOptionsListSelector(),
		({ ordersWaysInfo }) => {
			const baseInitialValues = {
				group_id: ordersWaysInfo?.group.id ?? '',
				name: ordersWaysInfo?.name ?? '',
				weight: ordersWaysInfo?.weight ?? '',
				volume: ordersWaysInfo?.volume ?? '',
				weight_recycling_rate: ordersWaysInfo?.weight_recycling_rate ?? '',
				volume_recycling_rate: ordersWaysInfo?.volume_recycling_rate ?? '',
				weight_rate: ordersWaysInfo?.weight_rate ?? '',
				volume_rate: ordersWaysInfo?.volume_rate ?? '',
			};

			return {
				...baseInitialValues,
			};
		},
	);

export const getIsOrdersWaysInfoLoadingSelector = () =>
	createSelector(
		selectOrdersWaysModalDomain,
		({ isOrdersWaysInfoLoading, isOrdersWaysDeleting, isOrdersWaysSaving }) =>
			isOrdersWaysInfoLoading || isOrdersWaysDeleting || isOrdersWaysSaving,
	);

export const getFormIsInvalidSelector = () => createSelector(isInvalid(FORM_NAME), isInvalid => isInvalid);

export const getIsModalShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'order-way');
	});

export const getIsAuditTabShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'order-way' && query?.tab === 'audit');
	});

export const getIsOrdersWaysAddFormOpenSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'order-way' && query?.mode === 'add');
	});

export const getIsOrdersWaysEditFormOpenSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'order-way' && Boolean(query?.id) && query?.mode === 'edit');
	});

export const getDataForCreateOrUpdateOrdersWaysSelector = () =>
	createSelector(getFormValues(FORM_NAME), formValues => {
		const { shipper, carrier, ...othersFormValues } = formValues;

		const settings = Object.entries(othersFormValues)
			.filter(([key]) => key.startsWith('settings '))
			.map(([key, value]) => ({
				category_id: Number(key.slice(9)),
				setting_id: value === 'none' ? null : value,
			}));

		const filteredOthersFormValues = Object.fromEntries(
			Object.entries(othersFormValues).filter(([key]) => !key.startsWith('settings ')),
		);

		return {
			...filteredOthersFormValues,
			from_id: shipper,
			to_id: carrier,
			settings: settings.sort((a, b) => a.category_id - b.category_id),
		};
	});

export const getIsAvailableOrdersGroupsUpdateSelector = () =>
	createSelector(selectOrdersWaysModalDomain, ({ ordersWaysInfo }) => {
		return Boolean(!ordersWaysInfo?.planning);
	});
